/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useMemo } from "react";
import MainLayout from "../../../layouts/MainLayout";
import { useNavigate } from "react-router-dom";
import "../../../assets/styles/custom-styles.css";
import Form from "../../../components/forms/Form";
import useLoader from "../../../hooks/useLoader";
import useNotification from "../../../hooks/useNotification";
import useForm from "../../../hooks/useForm";
import { createSupplierFields } from "../../../constants/forms/supplierFields";
import { maskCep, removeMask, maskCnpj, removeMaskCpfCnpj } from "../../../utils/maskUtils";
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../utils/objectUtils";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import SectionHeader from "../../../components/forms/SectionHeader";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useRemoteSelectOptions from "../../../hooks/useRemoteSelectOptions";
import useSelectOptions from "../../../hooks/useSelectOptions";

const CreateSupplierPage = () => {
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();
  const {
    post: baseServicePost,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);
  const { formData, setFormData, handleChange, resetForm } = useForm(
    setDefaultFieldValues(createSupplierFields),
  );

  const handleCepChange = useCallback(
    async (fieldId, value) => {
      const maskedValue = maskCep(value);
      const zip = removeMask(maskedValue);

      handleChange(fieldId, maskedValue);

      if (zip.length === 8) {
        showLoader();
        try {
          const response = await fetch(`https://viacep.com.br/ws/${zip}/json/`);
          if (!response.ok) throw new Error("Erro ao buscar o CEP");

          const data = await response.json();
          if (data.erro) throw new Error("CEP não encontrado");

          setFormData((prev) => ({
            ...prev,
            address: {
              ...prev.address,
              street: data.logradouro || "",
              district: data.bairro || "",
              city: data.localidade || "",
              state: data.estado || "",
              country: "Brasil" || "",
            },
          }));

          showNotification("success", "Endereço preenchido automaticamente!");
        } catch (error) {
          showNotification("error", error.message);
        } finally {
          hideLoader();
        }
      }
    },
    [handleChange, setFormData, showLoader, hideLoader, showNotification],
  );

  const stateSelect = useRemoteSelectOptions({
    queryKey: ["stateOptions"],
    fetcher: async () =>
      await fetch(`https://brasilapi.com.br/api/ibge/uf/v1`).then((res) =>
        res.json(),
      ),
    fieldId: "address.state",
    formData,
    isMulti: false,
    mapToOption: (type) => ({ value: type.nome, label: type.nome }),
  });

  const rawCountryOptions = useMemo(
    () => [{ value: "Brasil", label: "Brasil" }],
    [],
  );

  const countrySelect = useSelectOptions({
    fieldId: "address.country",
    formData,
    rawOptions: rawCountryOptions,
    isMulti: false,
    mapToOption: (item) => ({ value: item.value, label: item.label }),
  });

  const selects = {
    "address.state": stateSelect,
    "address.country": countrySelect,
  };

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [selects],
  );

  // --- Funções para Selects (getOptions e getSelectedValue) ---
  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.options || [];
    },
    [selects],
  );

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [selects],
  );

  const createSupplierMutation = useMutation({
    mutationFn: async (data) => {
      showLoader();
      const response = await baseServicePost(entities.suppliers.create, data);
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 201) {
        hideLoader();
        resetForm();
        navigate("/almoxarifado/fornecedores");
        queryClient.invalidateQueries({ queryKey: ["almox_suppliers"] });
      }
    },
    onError: (error) => {
      console.log("onError ", error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      showLoader();

      const sanitizedData = {
        supplier: {
          ...formData.supplier,
          cpf_cnpj: removeMaskCpfCnpj(formData.supplier.cpf_cnpj),
          phone: removeMask(formData.supplier.phone),
        },
        address: {
          ...formData.address,
          zip: removeMask(formData.address.zip),
        },
        contact: {
          ...formData.contact,
          phone: removeMask(formData.contact.phone),
          cell: removeMask(formData.contact.cell),
        },
      };

      await createSupplierMutation.mutateAsync(sanitizedData);
    } catch (error) {
      console.error("Erro ao cadastrar fornecedor:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, createSupplierMutation, showLoader]);

  const handleBack = useCallback(() => {
    navigate(`/almoxarifado/fornecedores`);
  }, [navigate]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Cadastro de Fornecedores"
        showBackButton={true}
        backUrl={`/almoxarifado/fornecedores`}
      />
      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          textSubmit="Cadastrar"
          textLoadingSubmit="Cadastrando..."
          handleBack={handleBack}
        >
          {() => {
            const renderedSections = new Set();

            return createSupplierFields.map((section, index) => {
              const sectionType = section.section.split(" - ")[0];
              const showHeader = !renderedSections.has(sectionType);

              if (showHeader) {
                renderedSections.add(sectionType);
              }

              return (
                <div key={section.section + " " + index}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={false}
                      setViewTable={() => {}}
                      addFieldsInData={() => {}}
                    />
                  )}

                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={(fieldId, value) => {
                      if (fieldId === "address.zip") {
                        handleCepChange(fieldId, value);
                      } else {
                        handleChange(fieldId, value);
                      }
                    }}
                    getOptions={getOptions}
                    getSelectedValue={getSelectedValue}
                    getSelectMeta={getSelectMeta}
                  />
                </div>
              );
            });
          }}
        </Form>
      </div>
    </MainLayout>
  );
};

export default CreateSupplierPage;
